import React, { useState, useCallback } from 'react'; // Import useCallback
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Grid,
  Button, // Import Button
  IconButton, // Import IconButton
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Import Copy icon
import CheckIcon from '@mui/icons-material/Check'; // Import Check icon
import LinkIcon from '@mui/icons-material/Link'; // Import Link icon for the button
import { FirebaseEvent, Gig } from 'shared/types/appTypes';
import { doc, getDoc } from 'firebase/firestore';
import { db } from 'src/firebase/firebaseConfig';
import { statusesWithPreliminaryDetails, statusesWithFullDetails } from 'src/constants/appConstants';

interface GigDetailsProps {
  gig: Gig;
}

// Wrap component definition with React.memo
const GigDetails: React.FC<GigDetailsProps> = React.memo(({ gig }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [prelimExpanded, setPrelimExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [prelimLoading, setPrelimLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<FirebaseEvent | null>(null);
  const [prelimDetails, setPrelimDetails] = useState<FirebaseEvent | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [prelimErrorMessage, setPrelimErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if the gig status should show preliminary details
  const shouldShowPreliminaryDetails = gig.status && statusesWithPreliminaryDetails.includes(gig.status);

  // Check if the gig status should show full details
  const shouldShowFullDetails = gig.status && statusesWithFullDetails.includes(gig.status);

  // Memoize handleChange for full details
  const handleChange = useCallback(
    async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded);
      setErrorMessage(null);
      setCopied(false);

      // Check details, loading, errorMessage state as dependencies
      if (isExpanded && !details && !loading && !errorMessage) {
        // Check gig.eventId as dependency
        if (!gig.eventId) {
          setErrorMessage('Error: Event ID is missing for this gig.');
          return;
        }
        setLoading(true);
        try {
          const eventDocRef = doc(db, 'events', gig.eventId);
          const docSnap = await getDoc(eventDocRef);

          if (docSnap.exists()) {
            const eventData = docSnap.data() as FirebaseEvent;
            console.log(`Fetched details for event: ${gig.eventId}`, eventData);
            setDetails(eventData);
          } else {
            console.log(`No event document found for ID: ${gig.eventId}`);
            setErrorMessage(`No details found for event ID: ${gig.eventId}`);
          }
        } catch (error) {
          console.error('Error fetching event details:', error);
          const message = `Error loading details: ${error instanceof Error ? error.message : 'Unknown error'}`;
          setErrorMessage(message);
        } finally {
          setLoading(false);
        }
      }
    },
    [gig.eventId, details, loading, errorMessage]
  ); // Add dependencies

  // Memoize handlePrelimChange for preliminary details
  const handlePrelimChange = useCallback(
    async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setPrelimExpanded(isExpanded);
      setPrelimErrorMessage(null);

      // Check prelimDetails, prelimLoading, prelimErrorMessage state as dependencies
      if (isExpanded && !prelimDetails && !prelimLoading && !prelimErrorMessage) {
        // Check gig.eventId as dependency
        if (!gig.eventId) {
          setPrelimErrorMessage('Error: Event ID is missing for this gig.');
          return;
        }
        setPrelimLoading(true);
        try {
          const eventDocRef = doc(db, 'events', gig.eventId);
          const docSnap = await getDoc(eventDocRef);

          if (docSnap.exists()) {
            const eventData = docSnap.data() as FirebaseEvent;
            console.log(`Fetched preliminary details for event: ${gig.eventId}`, eventData);
            setPrelimDetails(eventData);
          } else {
            console.log(`No event document found for ID: ${gig.eventId}`);
            setPrelimErrorMessage(`No details found for event ID: ${gig.eventId}`);
          }
        } catch (error) {
          console.error('Error fetching preliminary event details:', error);
          const message = `Error loading details: ${error instanceof Error ? error.message : 'Unknown error'}`;
          setPrelimErrorMessage(message);
        } finally {
          setPrelimLoading(false);
        }
      }
    },
    [gig.eventId, prelimDetails, prelimLoading, prelimErrorMessage]
  ); // Add dependencies

  const getExtraFieldValue = (data: FirebaseEvent | null, key: string): { label?: string; value?: any } => {
    if (!data?.dayliteData?.extra_fields) {
      return {};
    }
    const fieldData = data.dayliteData.extra_fields[key as keyof typeof data.dayliteData.extra_fields];
    return {
      label: fieldData?.display_name,
      value: fieldData?.value
    };
  };

  const extraFieldKey4 = 'com.marketcircle.daylite/extra4';
  const extraFieldKey7 = 'com.marketcircle.daylite/extra7';

  const field4 = getExtraFieldValue(details, extraFieldKey4);
  const field7 = getExtraFieldValue(details, extraFieldKey7);

  // Also get field4 for preliminary details
  const prelimField4 = getExtraFieldValue(prelimDetails, extraFieldKey4);

  // Memoize handleCopyLink
  const handleCopyLink = useCallback(() => {
    // Check field7.value as dependency
    if (field7.value) {
      navigator.clipboard
        .writeText(field7.value)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
        });
    }
  }, [field7.value]); // Add dependency

  const isUrl = (value: any): value is string => typeof value === 'string' && value.startsWith('http');

  return (
    <>
      {/* Preliminary Details Accordion - for early statuses */}
      {shouldShowPreliminaryDetails && (
        <Accordion
          expanded={prelimExpanded}
          onChange={handlePrelimChange}
          sx={{ mt: 2, '&.Mui-expanded:before': { opacity: 1 } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`prelim-panel-${gig.id}-content`}
            id={`prelim-panel-${gig.id}-header`}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              width: '100%',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 0.5 : 0
            }}>
              <Typography sx={{ 
                flexShrink: 0,
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                Preliminary Details
              </Typography>
              {!prelimExpanded && (
                <Chip
                  icon={<InfoIcon />}
                  label="Early venue and timing info"
                  color="warning"
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    mx: isMobile ? 0 : 2,
                    mt: isMobile ? 0.5 : 0
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {prelimLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50px' }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Loading preliminary details...</Typography>
              </Box>
            ) : prelimErrorMessage ? (
              <Typography color="error">{prelimErrorMessage}</Typography>
            ) : prelimDetails ? (
              <Grid container spacing={2} alignItems="flex-start">
                {/* Venue Info */}
                {prelimDetails.venueInfo && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Venue Info
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {prelimDetails.venueInfo}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Timing Info */}
                {prelimDetails.timingInfo && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Timing Info
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {prelimDetails.timingInfo}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Entertainment Type (field4) */}
                {prelimField4.label && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {prelimField4.label}
                      </Typography>
                      <Typography variant="body1">
                        {prelimField4.value !== undefined && prelimField4.value !== null && prelimField4.value !== ''
                          ? prelimField4.value
                          : '-'}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {!prelimDetails.venueInfo && !prelimDetails.timingInfo && !prelimField4.label && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary">No preliminary details available yet.</Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography color="text.secondary">Expand to load preliminary details.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Full Details Accordion - for confirmed statuses */}
      {shouldShowFullDetails && (
        <Accordion expanded={expanded} onChange={handleChange} sx={{ mt: 2, '&.Mui-expanded:before': { opacity: 1 } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${gig.id}-content`}
            id={`panel-${gig.id}-header`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ flexShrink: 0 }}>Details</Typography>
              {!expanded && gig.status === 'Booked' && (
                <Chip
                  icon={<InfoIcon />}
                  label="Preliminary details available - subject to change"
                  color="info"
                  variant="outlined"
                  sx={{ mx: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50px' }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 1 }}>Loading details...</Typography>
              </Box>
            ) : errorMessage ? (
              <Typography color="error">{errorMessage}</Typography>
            ) : details ? (
              <Grid container spacing={2} alignItems="flex-start">
                {/* Venue Info */}
                {details.venueInfo && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Venue Info
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {details.venueInfo}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Timing Info */}
                {details.timingInfo && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Timing Info
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {details.timingInfo}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Existing Daylite Extra Fields */}
                {/* Entertainment Type (field4) - shown in both preliminary and full details */}
                {field4.label && (
                  <Grid item xs={12} sm={field7.label && isUrl(field7.value) ? 6 : 12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {field4.label}
                      </Typography>
                      <Typography variant="body1">
                        {field4.value !== undefined && field4.value !== null && field4.value !== ''
                          ? field4.value
                          : '-'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {/* Production Sheet (field7) - only in full details */}
                {field7.label && (
                  <Grid item xs={12} sm={field4.label ? 6 : 12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {field7.label}
                      </Typography>
                      {isUrl(field7.value) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LinkIcon />}
                            href={field7.value}
                            target="_blank" // Open in new tab
                            rel="noopener noreferrer" // Security best practice
                          >
                            Open
                          </Button>
                          <IconButton
                            size="small"
                            onClick={handleCopyLink}
                            color={copied ? 'success' : 'default'} // Change color on copy
                            aria-label="Copy link"
                          >
                            {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="body1">
                          {field7.value !== undefined && field7.value !== null && field7.value !== ''
                            ? field7.value
                            : '-'}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                {!details.venueInfo && !details.timingInfo && !field4.label && !field7.label && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary">No additional details available for this event.</Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography color="text.secondary">Expand to load details.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}); // Close React.memo

export default GigDetails;
